import { computed } from 'vue';
import { useStore } from 'vuex';

/**
 * The 5-rung mentor selling-readiness ladder. Mirrors the backend
 * ``SellingReadiness`` TextChoices and is delivered to the FE as the
 * snake_case field ``user.selling_readiness`` on the current-user payload
 * (``UserMeSerializer.selling_readiness``).
 */
export type SellingReadinessValue =
    | 'not_mentor'
    | 'application_pending'
    | 'application_rejected'
    | 'approved_no_payout'
    | 'selling';

const FREE_OR_BETTER: SellingReadinessValue[] = ['approved_no_payout', 'selling'];

/**
 * Reactive view over the current user's selling-readiness rung plus the
 * derived publish/payout predicates. Reads from the single Vuex store at
 * ``store.state.user`` (NOT a ``userProfile`` sub-object) and the snake_case
 * field ``selling_readiness``. Defaults to ``'not_mentor'`` when the user is
 * absent or the field predates this feature.
 */
export function useSellingReadiness() {
    const store = useStore();

    const value = computed<SellingReadinessValue>(
        () => (store.state.user?.selling_readiness as SellingReadinessValue) ?? 'not_mentor',
    );

    const isSelling = computed(() => value.value === 'selling');
    const isMentor = computed(() => value.value !== 'not_mentor');
    const canPublishPaid = computed(() => value.value === 'selling');
    const canPublishFree = computed(() => FREE_OR_BETTER.includes(value.value));
    const canAttachPayout = computed(() => FREE_OR_BETTER.includes(value.value));

    return {
        value,
        isSelling,
        isMentor,
        canPublishPaid,
        canPublishFree,
        canAttachPayout,
    };
}
